/**
 * Profile Page - Redesigned
 *
 * Modern user profile with enhanced UI/UX, preferences, and personalization.
 * Features glassmorphism design, smooth animations, and improved visual hierarchy.
 */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { backendAPI } from '../api/backend';
import { useNavigationStack } from '../contexts/NavigationContext';
import { handleImageError } from '../utils/imageUtils';
import './ProfileNew.css';

const Profile = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const { navigateWithTransition } = useNavigationStack();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // overview, preferences, activity
  const [forYouProducts, setForYouProducts] = useState([]);

  // Delete account state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Activity state
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [activitiesError, setActivitiesError] = useState(null);

  // Credits state
  const [credits, setCredits] = useState(null);
  const [creditsLoading, setCreditsLoading] = useState(false);
  const [purchasingPackage, setPurchasingPackage] = useState(null);

  // Preferences state
  const [preferences, setPreferences] = useState({
    brand_preferences: [],
    color_preferences: [],
    price_sensitivity: 0.5,
    style_preferences: {
      preferred_styles: [],
      avoid_styles: [],
      style_confidence: 0.8
    }
  });

  // Load profile on mount
  useEffect(() => {
    if (!currentUser) {
      navigate('/auth');
      return;
    }

    loadProfile();
  }, [currentUser, navigate]);

  // Load activities when activity tab is opened
  useEffect(() => {
    if (activeTab === 'activity' && currentUser) {
      loadActivities();
    }
  }, [activeTab, currentUser]);

  // Load credits when credits tab is opened
  useEffect(() => {
    if (activeTab === 'credits' && currentUser) {
      loadCredits();
    }
  }, [activeTab, currentUser]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const data = await backendAPI.getUserProfile();
      setProfile(data.user);

      // Set preferences
      if (data.user.preferences) {
        setPreferences({
          brand_preferences: data.user.preferences.brand_preferences || [],
          color_preferences: data.user.preferences.color_preferences || [],
          price_sensitivity: data.user.preferences.price_sensitivity || 0.5,
          style_preferences: data.user.preferences.style_preferences || {
            preferred_styles: [],
            avoid_styles: [],
            style_confidence: 0.8
          }
        });
      }

      // Load personalized products
      loadForYouProducts();
    } catch (err) {
      console.error('Failed to load profile:', err);

      // Check if this is an authentication error
      if (err.response?.status === 401) {
        setError('Profile features are coming soon! Stay tuned for personalized recommendations.');
      } else {
        setError('Unable to load profile. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadForYouProducts = async () => {
    try {
      // Get personalized recommendations from /api/for-you
      const response = await backendAPI.getForYouRecommendations(3);

      console.log('[For You] API response:', response);

      if (response.success && response.recommendations && response.recommendations.length > 0) {
        // Map API response to component format
        const products = response.recommendations.map(rec => ({
          title: rec.product_name || 'Fashion Item',
          link: rec.product_url || '#',
          thumbnail: rec.image_url || '',
          image: rec.image_url || '',
          price: rec.price ? `$${rec.price}` : '',
          brand: rec.brand || '',
          data_source: rec.data_source || 'unknown'
        }));

        console.log('[For You] Mapped products:', products);
        setForYouProducts(products);
      } else {
        console.log('[For You] No recommendations, using empty state');
        setForYouProducts([]);
      }
    } catch (err) {
      console.error('Failed to load For You products:', err);
      // Show empty state instead of mock data
      setForYouProducts([]);
    }
  };

  const loadActivities = async () => {
    try {
      setActivitiesLoading(true);
      setActivitiesError(null);

      const data = await backendAPI.getActivities(15);
      setActivities(data.activities || []);
    } catch (err) {
      console.error('Failed to load activities:', err);
      setActivitiesError('Unable to load activity history');
    } finally {
      setActivitiesLoading(false);
    }
  };

  const loadCredits = async () => {
    try {
      setCreditsLoading(true);
      const data = await backendAPI.getCreditsBalance();
      setCredits(data);
    } catch (err) {
      console.error('Failed to load credits:', err);
      // Set default values if API fails
      setCredits({
        session_remaining: 4000,
        daily_remaining: 8000,
        session_limit: 4000,
        daily_limit: 8000,
        is_premium: false
      });
    } finally {
      setCreditsLoading(false);
    }
  };

  const handlePurchaseCredits = async (packageType) => {
    try {
      setPurchasingPackage(packageType);
      setError(null);

      const data = await backendAPI.purchaseCredits(packageType);

      if (data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      console.error('Failed to purchase credits:', err);
      setError('Unable to start checkout. Please try again.');
    } finally {
      setPurchasingPackage(null);
    }
  };

  const handleSavePreferences = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      await backendAPI.updatePreferences(preferences);

      setSuccessMessage('Preferences saved successfully! 🎉');
      setTimeout(() => setSuccessMessage(null), 3000);

      // Log activity for preference update
      const updatedFields = [];
      if (preferences.brand_preferences?.length > 0) updatedFields.push('brands');
      if (preferences.color_preferences?.length > 0) updatedFields.push('colors');
      if (preferences.style_preferences?.preferred_styles?.length > 0) updatedFields.push('styles');

      backendAPI.logActivity(
        'preference_update',
        'Updated style preferences',
        {
          fields: updatedFields,
          timestamp: new Date().toISOString()
        }
      );
    } catch (err) {
      console.error('Failed to save preferences:', err);
      setError('Failed to save preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/');
    } catch (err) {
      console.error('Sign out failed:', err);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      return;
    }

    try {
      setDeleting(true);
      setError(null);

      // Call backend to delete account
      await backendAPI.deleteAccount();

      // Sign out after successful deletion
      await logout();

      // Navigate to home with success message
      navigate('/', { state: { message: 'Your account has been deleted successfully.' } });
    } catch (err) {
      console.error('Failed to delete account:', err);
      setError('Failed to delete account. Please try again or contact support.');
      setDeleting(false);
    }
  };

  const toggleBrand = (brand) => {
    setPreferences(prev => ({
      ...prev,
      brand_preferences: prev.brand_preferences.includes(brand)
        ? prev.brand_preferences.filter(b => b !== brand)
        : [...prev.brand_preferences, brand]
    }));
  };

  const toggleColor = (color) => {
    setPreferences(prev => ({
      ...prev,
      color_preferences: prev.color_preferences.includes(color)
        ? prev.color_preferences.filter(c => c !== color)
        : [...prev.color_preferences, color]
    }));
  };

  const toggleStyle = (style, type) => {
    setPreferences(prev => ({
      ...prev,
      style_preferences: {
        ...prev.style_preferences,
        [type]: prev.style_preferences[type].includes(style)
          ? prev.style_preferences[type].filter(s => s !== style)
          : [...prev.style_preferences[type], style]
      }
    }));
  };

  const popularBrands = ['Nike', 'Adidas', 'Zara', 'H&M', 'Gucci', 'Prada', 'Chanel', 'Dior', 'Supreme', 'Off-White', 'Versace', 'Burberry'];
  const popularColors = [
    { name: 'black', hex: '#000000' },
    { name: 'white', hex: '#FFFFFF' },
    { name: 'navy', hex: '#001F3F' },
    { name: 'gray', hex: '#808080' },
    { name: 'beige', hex: '#F5F5DC' },
    { name: 'red', hex: '#FF0000' },
    { name: 'pink', hex: '#FFC0CB' },
    { name: 'green', hex: '#008000' },
    { name: 'blue', hex: '#0000FF' },
    { name: 'brown', hex: '#8B4513' }
  ];
  const styleOptions = ['casual', 'formal', 'streetwear', 'sporty', 'vintage', 'business', 'bohemian', 'minimalist', 'athleisure', 'chic'];

  // Helper function to format activity timestamp as relative time
  const formatActivityTime = (timestamp) => {
    if (!timestamp) return 'Recently';

    const now = new Date();
    const activityDate = new Date(timestamp);
    const diffInSeconds = Math.floor((now - activityDate) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 604800)} weeks ago`;
    return activityDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // User statistics (from backend or defaults to 0 for new users)
  const stats = {
    searches: profile?.stats?.search_count || 0,
    favorites: profile?.stats?.favorites_count || 0,
    matches: profile?.stats?.matches_count || 0
  };

  if (loading) {
    return (
      <div className="profile-page">
        <div className="profile-loading">
          <div className="loading-spinner"></div>
          <p>Loading your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      {/* Animated Background */}
      <div className="profile-bg-animation">
        <div className="bg-gradient-1"></div>
        <div className="bg-gradient-2"></div>
        <div className="bg-gradient-3"></div>
      </div>

      {/* Navigation Header */}
      <header className="profile-nav">
        <div className="profile-nav-content">
          <Link to="/" className="nav-link">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Home
          </Link>
          <button className="signout-btn" onClick={handleSignOut}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            Sign Out
          </button>
        </div>
      </header>

      <div className="profile-container">
        {/* Hero Section with User Info */}
        <motion.div
          className="profile-hero"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="hero-content">
            <div className="profile-avatar-wrapper">
              <div className="avatar-ring"></div>
              {currentUser?.photoURL ? (
                <img src={currentUser.photoURL} alt="Profile" className="profile-avatar-img" />
              ) : (
                <div className="profile-avatar-placeholder">
                  {currentUser?.email?.charAt(0).toUpperCase() || '?'}
                </div>
              )}
              <div className="avatar-status"></div>
            </div>

            <div className="profile-info">
              <h1 className="profile-title">
                {profile?.display_name || currentUser?.displayName || 'Fashion Enthusiast'}
              </h1>
              <p className="profile-subtitle">{currentUser?.email}</p>
              {profile?.can_personalize && (
                <div className="profile-badge-premium">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                  Premium Member
                </div>
              )}
            </div>
          </div>

          {/* Stats Cards */}
          <div className="stats-grid">
            <motion.div
              className="stat-card"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
            >
              <div className="stat-icon stat-icon-searches">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
              </div>
              <div className="stat-value">{stats.searches}</div>
              <div className="stat-label">Searches</div>
            </motion.div>

            <motion.div
              className="stat-card stat-card-clickable"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              onClick={() => navigate('/favorites')}
              style={{ cursor: 'pointer' }}
            >
              <div className="stat-icon stat-icon-favorites">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
              </div>
              <div className="stat-value">{stats.favorites}</div>
              <div className="stat-label">Favorites</div>
            </motion.div>

            <motion.div
              className="stat-card"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
            >
              <div className="stat-icon stat-icon-matches">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <div className="stat-value">{stats.matches}</div>
              <div className="stat-label">Matches</div>
            </motion.div>
          </div>
        </motion.div>

        {/* Tabs Navigation */}
        <motion.div
          className="profile-tabs"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <button
            className={`tab-btn ${activeTab === 'overview' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="7" height="7" />
              <rect x="14" y="3" width="7" height="7" />
              <rect x="14" y="14" width="7" height="7" />
              <rect x="3" y="14" width="7" height="7" />
            </svg>
            Overview
          </button>
          <button
            className={`tab-btn ${activeTab === 'preferences' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('preferences')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="3" />
              <path d="M12 1v6m0 6v6m9-9h-6m-6 0H3" />
            </svg>
            Preferences
          </button>
          <button
            className={`tab-btn ${activeTab === 'activity' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('activity')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
            Activity
          </button>
          <button
            className={`tab-btn ${activeTab === 'credits' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('credits')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 6v12M6 12h12" />
            </svg>
            Credits
          </button>
        </motion.div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <motion.div
              key="overview"
              className="tab-content"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="content-grid">
                <div className="content-card">
                  <h3 className="card-title">Quick Stats</h3>
                  <div className="quick-stats">
                    <div className="quick-stat-item">
                      <span className="quick-stat-label">Member Since</span>
                      <span className="quick-stat-value">
                        {profile?.created_at
                          ? new Date(profile.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                          : 'Recently'
                        }
                      </span>
                    </div>
                    <div className="quick-stat-item">
                      <span className="quick-stat-label">Avg. Matches</span>
                      <span className="quick-stat-value">
                        {stats.searches > 0 ? Math.round(stats.matches / stats.searches) : 0} per search
                      </span>
                    </div>
                    <div className="quick-stat-item">
                      <span className="quick-stat-label">Style Profile</span>
                      <span className="quick-stat-value">
                        {preferences.style_preferences.preferred_styles.length > 0
                          ? preferences.style_preferences.preferred_styles[0]
                          : 'Not set'
                        }
                      </span>
                    </div>
                  </div>
                </div>

                <div className="content-card">
                  <h3 className="card-title">Your Style DNA</h3>
                  <div className="style-dna">
                    {preferences.brand_preferences.length > 0 && (
                      <div className="dna-item">
                        <span className="dna-icon">👗</span>
                        <span className="dna-text">{preferences.brand_preferences.length} favorite brands</span>
                      </div>
                    )}
                    {preferences.color_preferences.length > 0 && (
                      <div className="dna-item">
                        <span className="dna-icon">🎨</span>
                        <span className="dna-text">{preferences.color_preferences.length} preferred colors</span>
                      </div>
                    )}
                    {preferences.style_preferences.preferred_styles.length > 0 && (
                      <div className="dna-item">
                        <span className="dna-icon">✨</span>
                        <span className="dna-text">{preferences.style_preferences.preferred_styles.length} style preferences</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* For You Recommendations Section */}
              <div className="for-you-section">
                <div className="section-header">
                  <h3 className="section-title-large">
                    <span className="section-emoji">✨</span>
                    For You
                  </h3>
                  <p className="section-subtitle-small">Curated picks based on your style</p>
                </div>

                {forYouProducts.length > 0 ? (
                  <div className="for-you-grid">
                    {forYouProducts.map((product, index) => (
                      <motion.a
                        key={index}
                        href={product.link || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="for-you-card"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        whileHover={{ y: -8, transition: { duration: 0.2 } }}
                        onClick={(e) => {
                          if (!product.link || product.link === '#') {
                            e.preventDefault();
                            console.warn('Product has no valid link');
                          }
                        }}
                      >
                        <div className="for-you-image-wrapper">
                          <img
                            src={product.thumbnail || product.image || '/placeholder-product.svg'}
                            alt={product.title || 'Product'}
                            className="for-you-image"
                            loading="lazy"
                            data-original-src={product.thumbnail || product.image}
                            onError={(e) => handleImageError(e, product.thumbnail || product.image)}
                          />
                          <div className="for-you-overlay">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                              <polyline points="15 3 21 3 21 9"></polyline>
                              <line x1="10" y1="14" x2="21" y2="3"></line>
                            </svg>
                          </div>
                        </div>
                        <div className="for-you-content">
                          <h4 className="for-you-title">{product.title || 'Fashion Item'}</h4>
                          {product.price && (
                            <p className="for-you-price">{product.price}</p>
                          )}
                        </div>
                      </motion.a>
                    ))}
                  </div>
                ) : (
                  <div className="for-you-empty">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="M8 14s1.5 2 4 2 4-2 4-2"></path>
                      <line x1="9" y1="9" x2="9.01" y2="9"></line>
                      <line x1="15" y1="9" x2="15.01" y2="9"></line>
                    </svg>
                    <p>Complete your preferences to get personalized recommendations!</p>
                    <button
                      className="btn-secondary-small"
                      onClick={() => setActiveTab('preferences')}
                    >
                      Set Preferences
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Preferences Tab */}
          {activeTab === 'preferences' && (
            <motion.div
              key="preferences"
              className="tab-content"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Messages */}
              <AnimatePresence>
                {error && (
                  <motion.div
                    className="alert alert-error"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    {error}
                  </motion.div>
                )}

                {successMessage && (
                  <motion.div
                    className="alert alert-success"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    {successMessage}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Favorite Brands */}
              <div className="preference-section">
                <div className="section-header">
                  <h3 className="section-title-small">
                    <span className="section-emoji">👗</span>
                    Favorite Brands
                  </h3>
                  <span className="section-count">{preferences.brand_preferences.length} selected</span>
                </div>
                <div className="chips-grid">
                  {popularBrands.map(brand => (
                    <button
                      key={brand}
                      className={`chip-modern ${preferences.brand_preferences.includes(brand) ? 'chip-active' : ''}`}
                      onClick={() => toggleBrand(brand)}
                    >
                      {brand}
                      {preferences.brand_preferences.includes(brand) && (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Favorite Colors */}
              <div className="preference-section">
                <div className="section-header">
                  <h3 className="section-title-small">
                    <span className="section-emoji">🎨</span>
                    Favorite Colors
                  </h3>
                  <span className="section-count">{preferences.color_preferences.length} selected</span>
                </div>
                <div className="color-grid">
                  {popularColors.map(color => (
                    <button
                      key={color.name}
                      className={`color-chip ${preferences.color_preferences.includes(color.name) ? 'color-active' : ''}`}
                      onClick={() => toggleColor(color.name)}
                      title={color.name}
                    >
                      <div
                        className="color-swatch"
                        style={{ backgroundColor: color.hex }}
                      ></div>
                      <span className="color-name">{color.name}</span>
                      {preferences.color_preferences.includes(color.name) && (
                        <div className="color-check">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Preferred Styles */}
              <div className="preference-section">
                <div className="section-header">
                  <h3 className="section-title-small">
                    <span className="section-emoji">✨</span>
                    Preferred Styles
                  </h3>
                  <span className="section-count">{preferences.style_preferences.preferred_styles.length} selected</span>
                </div>
                <div className="chips-grid">
                  {styleOptions.map(style => (
                    <button
                      key={style}
                      className={`chip-modern ${preferences.style_preferences.preferred_styles.includes(style) ? 'chip-active' : ''}`}
                      onClick={() => toggleStyle(style, 'preferred_styles')}
                      disabled={preferences.style_preferences.avoid_styles.includes(style)}
                    >
                      {style}
                      {preferences.style_preferences.preferred_styles.includes(style) && (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Avoid Styles */}
              <div className="preference-section">
                <div className="section-header">
                  <h3 className="section-title-small">
                    <span className="section-emoji">🚫</span>
                    Avoid Styles
                  </h3>
                  <span className="section-count">{preferences.style_preferences.avoid_styles.length} selected</span>
                </div>
                <div className="chips-grid">
                  {styleOptions.map(style => (
                    <button
                      key={style}
                      className={`chip-modern chip-avoid ${preferences.style_preferences.avoid_styles.includes(style) ? 'chip-active' : ''}`}
                      onClick={() => toggleStyle(style, 'avoid_styles')}
                      disabled={preferences.style_preferences.preferred_styles.includes(style)}
                    >
                      {style}
                      {preferences.style_preferences.avoid_styles.includes(style) && (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price Sensitivity */}
              <div className="preference-section">
                <div className="section-header">
                  <h3 className="section-title-small">
                    <span className="section-emoji">💰</span>
                    Price Sensitivity
                  </h3>
                  <span className="section-count">{Math.round(preferences.price_sensitivity * 100)}%</span>
                </div>
                <div className="slider-container">
                  <div className="slider-labels">
                    <span>Budget</span>
                    <span>Balanced</span>
                    <span>Premium</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={preferences.price_sensitivity}
                    onChange={(e) => setPreferences(prev => ({
                      ...prev,
                      price_sensitivity: parseFloat(e.target.value)
                    }))}
                    className="modern-slider"
                  />
                  <div className="slider-value">
                    {preferences.price_sensitivity < 0.33 && '💵 Budget-Friendly'}
                    {preferences.price_sensitivity >= 0.33 && preferences.price_sensitivity < 0.67 && '⚖️ Balanced'}
                    {preferences.price_sensitivity >= 0.67 && '💎 Premium Quality'}
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <button
                className="save-btn-modern"
                onClick={handleSavePreferences}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <div className="spinner-small"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                      <polyline points="17 21 17 13 7 13 7 21" />
                      <polyline points="7 3 7 8 15 8" />
                    </svg>
                    Save Preferences
                  </>
                )}
              </button>
            </motion.div>
          )}

          {/* Activity Tab */}
          {activeTab === 'activity' && (
            <motion.div
              key="activity"
              className="tab-content"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Loading State */}
              {activitiesLoading && (
                <div className="activity-loading">
                  <div className="spinner"></div>
                  <p>Loading your activity...</p>
                </div>
              )}

              {/* Error State */}
              {activitiesError && (
                <div className="activity-error">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <p>{activitiesError}</p>
                </div>
              )}

              {/* Activities Feed */}
              {!activitiesLoading && !activitiesError && (
                <div className="activity-feed">
                  {activities.length > 0 ? (
                    <>
                      {activities.map((activity, index) => (
                        <motion.div
                          key={activity.id || index}
                          className="activity-item"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                        >
                          <div className={`activity-icon activity-icon-${activity.type}`}>
                            {/* Search activities */}
                            {(activity.type === 'search' || activity.type === 'search_summary') && (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8" />
                                <path d="m21 21-4.35-4.35" />
                              </svg>
                            )}
                            {/* Favorite activities */}
                            {(activity.type === 'favorite' || activity.type === 'favorites_summary' || activity.type === 'favorite_added') && (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                              </svg>
                            )}
                            {/* Preference/profile update activities */}
                            {(activity.type === 'preference_update' || activity.type === 'profile_update') && (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 20h9" />
                                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                              </svg>
                            )}
                            {/* Match activities */}
                            {(activity.type === 'match' || activity.type === 'matches_summary') && (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                <polyline points="22 4 12 14.01 9 11.01" />
                              </svg>
                            )}
                            {/* Account created */}
                            {activity.type === 'account_created' && (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                              </svg>
                            )}
                            {/* Login activity */}
                            {activity.type === 'login' && (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                                <polyline points="10 17 15 12 10 7" />
                                <line x1="15" y1="12" x2="3" y2="12" />
                              </svg>
                            )}
                          </div>
                          <div className="activity-content">
                            <p className="activity-title">{activity.title}</p>
                            <p className="activity-time">{formatActivityTime(activity.timestamp)}</p>
                          </div>
                        </motion.div>
                      ))}
                    </>
                  ) : (
                    <div className="empty-state">
                      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                      </svg>
                      <p>No activity yet. Start exploring to see your history here!</p>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          )}

          {/* Credits Tab */}
          {activeTab === 'credits' && (
            <motion.div
              key="credits"
              className="tab-content"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="credits-section">
                <h3 className="section-title">💬 AI Stylist Credits</h3>
                <p className="section-description">
                  Chat with Leo, your AI stylist. Credits are used for each conversation.
                </p>

                {/* Current Balance */}
                {creditsLoading ? (
                  <div className="credits-loading">
                    <div className="spinner"></div>
                    <p>Loading your credits...</p>
                  </div>
                ) : credits ? (
                  <div className="credits-balance-card">
                    <div className="balance-header">
                      <span className="balance-label">Current Balance</span>
                      {credits.is_premium && <span className="premium-badge">Premium</span>}
                    </div>
                    <div className="balance-stats">
                      <div className="balance-stat">
                        <span className="balance-value">{credits.session_remaining?.toLocaleString() || 0}</span>
                        <span className="balance-sublabel">Session Credits</span>
                        <div className="balance-bar">
                          <div
                            className="balance-bar-fill"
                            style={{ width: `${Math.min(100, ((credits.session_remaining || 0) / (credits.session_limit || 4000)) * 100)}%` }}
                          />
                        </div>
                      </div>
                      <div className="balance-stat">
                        <span className="balance-value">{credits.daily_remaining?.toLocaleString() || 0}</span>
                        <span className="balance-sublabel">Daily Credits</span>
                        <div className="balance-bar">
                          <div
                            className="balance-bar-fill"
                            style={{ width: `${Math.min(100, ((credits.daily_remaining || 0) / (credits.daily_limit || 8000)) * 100)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                    <p className="balance-note">Daily credits reset at midnight UTC</p>
                  </div>
                ) : null}

                {/* Purchase Options */}
                <h4 className="subsection-title">Get More Credits</h4>
                <div className="credits-packages">
                  <div className="credit-package">
                    <div className="package-header">
                      <span className="package-name">Starter</span>
                      <span className="package-tokens">10,000 tokens</span>
                    </div>
                    <div className="package-price">$2</div>
                    <p className="package-description">~20 conversations with Leo</p>
                    <button
                      className="package-btn"
                      onClick={() => handlePurchaseCredits('small')}
                      disabled={purchasingPackage}
                    >
                      {purchasingPackage === 'small' ? 'Processing...' : 'Buy Now'}
                    </button>
                  </div>

                  <div className="credit-package popular">
                    <div className="popular-badge">Best Value</div>
                    <div className="package-header">
                      <span className="package-name">Pro</span>
                      <span className="package-tokens">50,000 tokens</span>
                    </div>
                    <div className="package-price">$8</div>
                    <p className="package-description">~100 conversations with Leo</p>
                    <button
                      className="package-btn"
                      onClick={() => handlePurchaseCredits('medium')}
                      disabled={purchasingPackage}
                    >
                      {purchasingPackage === 'medium' ? 'Processing...' : 'Buy Now'}
                    </button>
                  </div>

                  <div className="credit-package">
                    <div className="package-header">
                      <span className="package-name">Unlimited</span>
                      <span className="package-tokens">100,000 tokens</span>
                    </div>
                    <div className="package-price">$15</div>
                    <p className="package-description">~200+ conversations with Leo</p>
                    <button
                      className="package-btn"
                      onClick={() => handlePurchaseCredits('large')}
                      disabled={purchasingPackage}
                    >
                      {purchasingPackage === 'large' ? 'Processing...' : 'Buy Now'}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Delete Account Section - Hidden at bottom */}
        <div className="delete-account-section">
          <button
            className="delete-account-link"
            onClick={() => setShowDeleteConfirm(true)}
          >
            Delete Account
          </button>
        </div>
      </div>

      {/* Delete Account Confirmation Modal */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            className="delete-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !deleting && setShowDeleteConfirm(false)}
          >
            <motion.div
              className="delete-modal"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="delete-modal-header">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ff4757" strokeWidth="2">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                <h3>Delete Account</h3>
              </div>
              <div className="delete-modal-body">
                <p>This action is <strong>permanent</strong> and cannot be undone.</p>
                <p>All your data will be deleted including:</p>
                <ul>
                  <li>Your profile and preferences</li>
                  <li>Saved favorites</li>
                  <li>Search history and activities</li>
                </ul>
                <p className="delete-confirm-instruction">
                  Type <strong>DELETE</strong> to confirm:
                </p>
                <input
                  type="text"
                  className="delete-confirm-input"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                  placeholder="Type DELETE"
                  disabled={deleting}
                />
              </div>
              <div className="delete-modal-actions">
                <button
                  className="delete-cancel-btn"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteConfirmText('');
                  }}
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  className="delete-confirm-btn"
                  onClick={handleDeleteAccount}
                  disabled={deleteConfirmText !== 'DELETE' || deleting}
                >
                  {deleting ? 'Deleting...' : 'Delete My Account'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Profile;

